import { Check } from 'lucide-react';

interface Step {
  id: string;
  label: string;
  description: string;
  isOptional?: boolean;
}

interface WizardSidebarProps {
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (index: number) => void;
}

export function WizardSidebar({ steps, currentStep, completedSteps, onStepClick }: WizardSidebarProps) {
  return (
    <div
      style={{
        width: '280px',
        flexShrink: 0,
        position: 'sticky',
        top: '2rem',
        alignSelf: 'flex-start',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          boxShadow: '0 4px 24px rgba(0,48,60,0.08)',
        }}
      >
        <h3
          style={{
            fontSize: '0.9rem',
            fontWeight: 700,
            color: '#00303c',
            marginBottom: '1.5rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          Configuration
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {steps.map((step, index) => {
            const isActive = currentStep === index;
            const isCompleted = completedSteps.has(index);
            const isAccessible = index <= currentStep || completedSteps.has(index);

            return (
              <div key={step.id} style={{ position: 'relative' }}>
                <button
                  onClick={() => onStepClick(index)}
                  disabled={!isAccessible}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    padding: '1rem',
                    background: isActive
                      ? 'linear-gradient(135deg, rgba(142,11,86,0.05) 0%, rgba(0,48,60,0.02) 100%)'
                      : 'transparent',
                    border: `2px solid ${isActive ? '#8E0B56' : 'transparent'}`,
                    borderRadius: '12px',
                    cursor: isAccessible ? 'pointer' : 'not-allowed',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    opacity: isAccessible ? 1 : 0.5,
                  }}
                  onMouseEnter={(e) => {
                    if (isAccessible && !isActive) {
                      e.currentTarget.style.background = 'rgba(142,11,86,0.03)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {/* Icône */}
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: isCompleted ? '#00758d' : isActive ? '#8E0B56' : '#f3f4f6',
                      color: isCompleted || isActive ? 'white' : '#999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '0.85rem',
                      fontWeight: 700,
                    }}
                  >
                    {isCompleted ? <Check size={18} /> : index + 1}
                  </div>

                  {/* Texte */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: isActive ? '#8E0B56' : '#666',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {step.label}
                      {step.isOptional && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            color: '#999',
                            marginLeft: '0.5rem',
                          }}
                        >
                          (optionnel)
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: '0.8rem',
                        color: '#999',
                        lineHeight: '1.3',
                      }}
                    >
                      {step.description}
                    </div>
                  </div>
                </button>

                {/* Ligne de connexion */}
                {index < steps.length - 1 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: '2rem',
                      top: '100%',
                      width: '2px',
                      height: '0.5rem',
                      background: isCompleted ? '#00758d' : '#e5e7eb',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
